import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAnalytics } from '../AnalyticsContext';
import { logEvent } from "firebase/analytics";

function AvisoDePrivacidad({ centerTitle }) {
    const analytics = useAnalytics();
    const appData = useSelector((state) => state.appData);
    const iframeRef = useRef(null);
    const [iframeHeight, setIframeHeight] = useState('600px');

    useEffect(() => {
        logEvent(analytics, "L_consulta_aviso", {
            screen_name: "Hamburguesa",
        });
    }, [analytics]);

    const decodeBase64 = (base64) => {
        if (!base64) return "";
        try {
            const text = atob(base64);
            const length = text.length;
            const bytes = new Uint8Array(length);
            for (let i = 0; i < length; i++) {
                bytes[i] = text.charCodeAt(i);
            }
            try {
                return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
            } catch (e) {
                return new TextDecoder('iso-8859-1').decode(bytes);
            }
        } catch (e) {
            try {
                return decodeURIComponent(escape(atob(base64)));
            } catch (err) {
                return atob(base64);
            }
        }
    };

    const getFontBaseUrl = () => {
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
        return publicUrl.startsWith('http://') || publicUrl.startsWith('https://')
            ? publicUrl
            : `${origin}${publicUrl}`;
    };

    const getFontCss = () => {
        const baseUrl = getFontBaseUrl();
        return `
@font-face {
    font-family: 'Museo Sans';
    src: url('${baseUrl}/assets/MuseoSans-100.otf') format('opentype');
    font-weight: normal;
    font-style: normal;
}
@font-face {
    font-family: 'Museo Sans';
    src: url('${baseUrl}/assets/MuseoSans-100.otf') format('opentype');
    font-weight: 100;
    font-style: normal;
}
@font-face {
    font-family: 'Museo Sans';
    src: url('${baseUrl}/assets/MuseoSans-100.otf') format('opentype');
    font-weight: 300;
    font-style: normal;
}
@font-face {
    font-family: 'Museo Sans';
    src: url('${baseUrl}/assets/MuseoSans-100.otf') format('opentype');
    font-weight: 400;
    font-style: normal;
}
@font-face {
    font-family: 'Museo Sans';
    src: url('${baseUrl}/assets/MuseoSans-100.otf') format('opentype');
    font-weight: 500;
    font-style: normal;
}
@font-face {
    font-family: 'Museo Sans';
    src: url('${baseUrl}/assets/MuseoSans_500.otf') format('opentype');
    font-weight: bold;
    font-style: normal;
}
@font-face {
    font-family: 'Museo Sans';
    src: url('${baseUrl}/assets/MuseoSans_500.otf') format('opentype');
    font-weight: 700;
    font-style: normal;
}
@font-face {
    font-family: 'boldSans';
    src: url('${baseUrl}/assets/MuseoSans_500.otf') format('opentype');
    font-weight: bold;
    font-style: normal;
}
html, body {
    font-family: 'Museo Sans', sans-serif !important;
    font-weight: normal;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
}
body *:not(.material-icons):not([class*="icon"]) {
    font-family: 'Museo Sans', sans-serif !important;
}
`;
    };

    const injectFontStyles = (html) => {
        if (!html) return "";
        const fontStyles = `<style id="app-font-style">${getFontCss()}</style>`;
        if (/<\/head>/i.test(html)) {
            return html.replace(/<\/head>/i, `${fontStyles}</head>`);
        } else if (/<body/i.test(html)) {
            return html.replace(/<body/i, `${fontStyles}<body`);
        }
        return `${fontStyles}${html}`;
    };

    const adjustHeight = () => {
        try {
            if (!iframeRef.current) return;
            const currentDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
            if (currentDoc) {
                const body = currentDoc.body;
                const html = currentDoc.documentElement;
                const scrollHeight = Math.max(
                    body ? body.scrollHeight : 0,
                    body ? body.offsetHeight : 0,
                    html ? html.scrollHeight : 0,
                    html ? html.offsetHeight : 0
                );
                if (scrollHeight > 50) {
                    setIframeHeight(`${scrollHeight + 25}px`);
                }
            }
        } catch (err) {
            console.error("Error al ajustar altura del iframe:", err);
        }
    };

    const handleIframeLoad = () => {
        try {
            if (iframeRef.current) {
                const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
                if (doc && doc.body) {
                    if (!doc.getElementById('app-font-style')) {
                        const style = doc.createElement('style');
                        style.id = 'app-font-style';
                        style.innerHTML = getFontCss();
                        (doc.head || doc.body || doc.documentElement).appendChild(style);
                    }

                    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
                        window.requestAnimationFrame(adjustHeight);
                    } else {
                        adjustHeight();
                    }

                    if (doc.fonts && doc.fonts.ready) {
                        doc.fonts.ready.then(() => {
                            if (typeof window !== 'undefined' && window.requestAnimationFrame) {
                                window.requestAnimationFrame(adjustHeight);
                            } else {
                                adjustHeight();
                            }
                        });
                    }
                    setTimeout(adjustHeight, 150);
                    setTimeout(adjustHeight, 400);
                }
            }
        } catch (err) {
            console.error("Error al manejar carga del iframe:", err);
        }
    };

    const rawContenidoHtml = appData.legales && appData.legales.Aviso ? decodeBase64(appData.legales.Aviso.contenido) : "";
    const contenidoHtml = useMemo(() => injectFontStyles(rawContenidoHtml), [rawContenidoHtml]);

    useEffect(() => {
        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
            window.requestAnimationFrame(adjustHeight);
        } else {
            adjustHeight();
        }
        const t1 = setTimeout(adjustHeight, 150);
        const t2 = setTimeout(adjustHeight, 400);
        const t3 = setTimeout(adjustHeight, 1000);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
        };
    }, [contenidoHtml]);

    if (appData.legales && appData.legales.Aviso) {
        const titulo = decodeBase64(appData.legales.Aviso.titulo);

        return (
            <div className="row contentLegal mb-0" style={{ width: '100%' }}>
                {/* {titulo && (
                    <div className={`col s12 left-align ft-18 labelNavy txtBold ${centerTitle ? 'center-align' : ''}`}>
                        {titulo}
                    </div>
                )} */}
                <div className="col s12 mt-1" style={{ width: '100%', padding: 0, paddingLeft: '1rem', boxSizing: 'border-box' }}>
                    {contenidoHtml ? (
                        <iframe
                            ref={iframeRef}
                            srcDoc={contenidoHtml}
                            title="Aviso de Privacidad"
                            onLoad={handleIframeLoad}
                            style={{
                                width: '100%',
                                height: iframeHeight,
                                border: 'none',
                                display: 'block',
                            }}
                        />
                    ) : (
                        <div className="col s12 align-justify mt-1">
                            No se encontró la información solicitada.
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="col s12 align-justify mt-1">
            No se encontró la información solicitada.
        </div>
    );
}

export default AvisoDePrivacidad;